import {
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationChannel, ConversationParticipation } from '@provision/entity-types';
import { gql, Reference, useMutation } from '@provision/graphql-client';
import { useCallback, useState } from 'react';
import {
  Conversation,
  ConversationParticipant,
} from 'src/graphql/entities/conversation/conversation.types';
import { CONVERSATION_CHANNEL_FIELDS } from 'src/graphql/entities/conversation-channel';
import {
  CONVERSATION_PARTICIPANT_FIELDS,
  ConversationParticipantItem,
} from 'src/graphql/entities/conversation-participants';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import {
  GET_CONVERSATION_PARTICIPATION_QUERY,
  GetConversationParticipation,
} from './hooks/use-conversation-participation-records';

export const CONVERSATION_PARTICIPATION_FIELDS = gql`
  fragment ConversationParticipationFields on ConversationParticipation {
    id
    partnerId
    isActive
    conversationId
    conversationChannelId
    participationMode
  }
`;

const CREATE_CONVERSATION_PARTICIPATION_MUTATION = gql`
  mutation CreateConversationParticipation(
    $partnerId: Int!
    $conversationId: String!
    $conversationChannelId: String!
    $participationMode: String!
  ) {
    createConversationParticipation(
      data: {
        partnerId: $partnerId
        conversationId: $conversationId
        conversationChannelId: $conversationChannelId
        participationMode: $participationMode
      }
    ) {
      ...ConversationParticipationFields
    }
  }
  ${CONVERSATION_PARTICIPATION_FIELDS}
`;

const UPDATE_CONVERSATION_PARTICIPATION_MUTATION = gql`
  mutation UpdateConversationParticipation(
    $conditions: [ConditionInput]
    $data: ConversationParticipationDataInput
  ) {
    updateConversationParticipation(conditions: $conditions, data: $data) {
      ...ConversationParticipationFields
    }
  }
  ${CONVERSATION_PARTICIPATION_FIELDS}
`;

const CREATE_PARTICIPANT_WITH_RELATED_DATA = gql`
  mutation CreateParticipantWithRelatedData($params: CreateParticipantWithRelatedDataInput!) {
    createParticipantWithRelatedData(params: $params) {
      participant {
        ...ConversationParticipantFields
      }
      channels {
        ...ConversationChannelFields
      }
      participations {
        ...ConversationParticipationFields
      }
    }
  }
  ${CONVERSATION_PARTICIPANT_FIELDS}
  ${CONVERSATION_CHANNEL_FIELDS}
  ${CONVERSATION_PARTICIPATION_FIELDS}
`;

export const useConversationParticipation = () => {
  const { showErrorAlert } = useNotifications();

  const [createConversationParticipantWithRelatedDataMutation] = useMutation<{
    createParticipantWithRelatedData: {
      participations: ConversationParticipation[];
      channels: ConversationChannel[];
      participant: ConversationParticipant;
    };
  }>(CREATE_PARTICIPANT_WITH_RELATED_DATA);

  const [createConversationParticipationMutation] = useMutation<{
    createConversationParticipation: ConversationParticipation;
  }>(CREATE_CONVERSATION_PARTICIPATION_MUTATION);

  const [updateConversationParticipationMutation] = useMutation<{
    updateConversationParticipation: ConversationParticipation;
  }>(UPDATE_CONVERSATION_PARTICIPATION_MUTATION);

  const createConversationParticipation = useCallback(
    async (data: {
      partnerId: number;
      conversationId: string;
      conversationChannelId: string;
      participationMode: ConversationParticipationMode;
      conversation: Conversation;
      channel: ConversationChannel;
      participant: ConversationParticipant | ConversationParticipantItem;
    }) => {
      const { conversation, channel, participant, ...variables } = data;

      const result = await createConversationParticipationMutation({
        variables,
        update(cache, r) {
          const participation = r.data?.createConversationParticipation;
          const id = cache.identify({
            __typename: 'ConversationParticipant',
            id: participant.id,
          });

          cache.modify({
            id,
            fields: {
              conversationChannels(existingItems = [], { toReference }) {
                const existingChannels = existingItems as ConversationChannel[];

                const newItem = {
                  conversationChannelTypeId: channel.conversationChannelTypeId,
                  externalIdentifier: channel.externalIdentifier,
                  id: channel.id,
                  __typename: 'ConversationChannel',
                };

                const reference = toReference(newItem, true);

                if (reference) {
                  return [reference, ...existingChannels];
                }

                return existingChannels;
              },
            },
          });

          cache.updateQuery<GetConversationParticipation, QueryVariables>(
            {
              query: GET_CONVERSATION_PARTICIPATION_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'conversationId',
                      operator: '=',
                      value: variables.conversationId,
                    },
                  ],
                },
              },
            },
            (existingData) => {
              if (!existingData?.getConversationParticipation || !participation) {
                return existingData;
              }

              const { records } = existingData.getConversationParticipation;

              const updatedRecords = [
                ...records,
                {
                  ...participation,
                },
              ];

              return {
                getConversationParticipation: {
                  ...existingData.getConversationParticipation,
                  records: updatedRecords,
                },
              };
            },
          );
        },
      });

      return result.data?.createConversationParticipation;
    },
    [createConversationParticipationMutation],
  );

  const deleteConversationParticipation = useCallback(
    async (data: {
      conversationChannelId: string;
      conversationId: string;
      conversation: Conversation;
    }) => {
      const { conversationChannelId, conversationId } = data;

      const result = await updateConversationParticipationMutation({
        variables: {
          conditions: [
            {
              attribute: 'conversationChannelId',
              operator: '=',
              value: conversationChannelId,
            },
            {
              attribute: 'conversationId',
              operator: '=',
              value: conversationId,
            },
          ],
          data: {
            isActive: false,
          },
        },
      });

      return result.data?.updateConversationParticipation;
    },
    [updateConversationParticipationMutation],
  );

  const updateConversationParticipation = useCallback(
    async (data: {
      conversationChannelId: string[];
      conversationId: string;
      conversation: Conversation;
      isActive: boolean;
    }) => {
      const { conversationChannelId, conversationId, isActive } = data;

      const result = await updateConversationParticipationMutation({
        variables: {
          conditions: [
            {
              attribute: 'conversationChannelId',
              operator: 'in',
              value: conversationChannelId.join(','),
            },
            {
              attribute: 'conversationId',
              operator: '=',
              value: conversationId,
            },
          ],
          data: {
            isActive,
          },
        },
      });

      return result.data?.updateConversationParticipation;
    },
    [updateConversationParticipationMutation],
  );

  const [isCreateWithRelatedDataLoading, setIsCreateWithRelatedDataLoading] = useState(false);

  const createParticipationWithRelatedData = useCallback(
    async (data: {
      partnerEmail?: string;
      conversation: Conversation;
      participantEntity: ConversationParticipantEntity;
      entityIdentifier: string;
      channels: { channelType: ConversationChannelType; externalIdentifier: string }[];
      participationMode: ConversationParticipationMode;
      conversationId: string;
      partnerId: number;
      onSuccess?: () => void;
    }) => {
      setIsCreateWithRelatedDataLoading(true);

      const { conversation, onSuccess, ...params } = data;
      const result = await createConversationParticipantWithRelatedDataMutation({
        variables: { params },
        onError: () => {
          showErrorAlert('Something went wrong during adding the participant');
          setIsCreateWithRelatedDataLoading(false);
        },

        update(cache, r) {
          const { participant, channels, participations } =
            r.data?.createParticipantWithRelatedData ?? {};

          if (!participant || !channels?.length || !participations?.length) {
            return;
          }

          const id = cache.identify({
            __typename: 'ConversationParticipant',
            id: participant.id,
          });

          const newChannels = channels.map((channel) => ({
            conversationChannelTypeId: channel.conversationChannelTypeId,
            externalIdentifier: channel.externalIdentifier,
            id: channel.id,
            conversationParticipantId: participant.id,
            __typename: 'ConversationChannel',
          }));

          cache.modify({
            id,
            fields: {
              conversationChannels(existingItems = [], { toReference }) {
                const existingRefs = existingItems as Reference[];
                const newRefs = newChannels.map((i) => toReference(i, true)).filter(Boolean);

                if (newRefs?.length) {
                  return [...newRefs, ...existingRefs].filter(
                    (channelRef, index, self) =>
                      index === self.findIndex((p) => p?.__ref === channelRef?.__ref),
                  );
                }

                return existingRefs;
              },
            },
          });

          const newParticipations = participations.map((participation) => {
            const channel = newChannels.find(
              (channel) => participation.conversationChannelId === channel.id,
            )!;

            return {
              ...participation,
              __typename: 'ConversationParticipation',
              conversationChannelId: channel.id,
              conversationId: conversation.id,
              partnerId: conversation.partnerId,
              isActive: participation.isActive,
              id: participation.id,
              participationMode: participation.participationMode,
              channel,
            };
          });

          cache.updateQuery<GetConversationParticipation, QueryVariables>(
            {
              query: GET_CONVERSATION_PARTICIPATION_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'conversationId',
                      operator: '=',
                      value: conversation.id,
                    },
                  ],
                },
              },
            },
            // @ts-expect-error need to adjust correct selection of channel's fields
            (existingData) => {
              if (!existingData?.getConversationParticipation || !newParticipations?.length) {
                return existingData;
              }

              const { records } = existingData.getConversationParticipation;

              const updatedRecords = [...records, ...newParticipations].filter(
                (participation, index, self) =>
                  index === self.findIndex((p) => p.id === participation.id),
              );

              return {
                getConversationParticipation: {
                  ...existingData.getConversationParticipation,
                  records: updatedRecords,
                },
              };
            },
          );

          onSuccess?.();
          setIsCreateWithRelatedDataLoading(false);
        },
      });

      return result.data?.createParticipantWithRelatedData;
    },
    [createConversationParticipantWithRelatedDataMutation, showErrorAlert],
  );

  const addParticipationWithChannel = useCallback(
    (data: {
      participantIdentity: string;
      email: string;
      conversation: Conversation;
      participantEntity: ConversationParticipantEntity;
    }) => {
      const { conversation, email, participantIdentity, participantEntity } = data;

      const participationMode =
        participantEntity === ConversationParticipantEntity.PlatformUser
          ? ConversationParticipationMode.Assist
          : ConversationParticipationMode.Engage;

      return createParticipationWithRelatedData({
        partnerEmail:
          conversation.partner.brandGroups?.[0]?.supportEmail ??
          conversation.partner.supportEmail,
        conversation,
        participantEntity,
        entityIdentifier: participantIdentity,
        channels: [{ channelType: ConversationChannelType.Email, externalIdentifier: email }],
        participationMode,
        conversationId: conversation.conversationId,
        partnerId: conversation.partnerId,
      });
    },
    [createParticipationWithRelatedData],
  );

  const deactivateParticipation = useCallback(
    (channel: ConversationChannel, conversation: Conversation) => {
      return updateConversationParticipation({
        conversationChannelId: [channel.id],
        conversationId: conversation.id,
        conversation,
        isActive: false,
      });
    },
    [updateConversationParticipation],
  );

  return {
    createConversationParticipation,
    deleteConversationParticipation,
    updateConversationParticipation,
    deactivateParticipation,
    addParticipationWithChannel,
    createParticipationWithRelatedData,
    isCreateWithRelatedDataLoading,
  };
};
